/* eslint-disable react-refresh/only-export-components -- Variant function export needed by shadcn/ui */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        default: 'btn-primary shadow-primary hover:shadow-primary-hover',
        destructive: 'bg-destructive text-white shadow-sm hover:bg-destructive/90 hover:scale-105',
        outline:
          'btn-secondary glass border hover:bg-primary/10 hover:text-primary hover:border-primary/50',
        secondary: 'btn-secondary hover:bg-primary/5',
        ghost: 'hover:bg-primary/10 hover:text-primary glass-hover',
        link: 'text-primary underline-offset-4 hover:underline hover:text-accent',
      },
      size: {
        default: 'h-11 px-6 py-2 has-[>svg]:px-5',
        sm: 'h-9 rounded-lg gap-1.5 px-4 has-[>svg]:px-3',
        lg: 'h-13 rounded-xl px-8 has-[>svg]:px-6 text-base',
        icon: 'size-11 rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
